import { fetchCurrentUser } from '../../ducks/user.duck';
import { listingsAPI } from '../../api';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import config from '../../config';

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/CreatorListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/CreatorListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/CreatorListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/CreatorListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/CreatorListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/CreatorListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/CreatorListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/CreatorListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/CreatorListingsPage/CLOSE_LISTING_ERROR';

export const DISCARD_DRAFT_REQUEST = 'app/CreatorListingsPage/DISCARD_DRAFT_REQUEST';
export const DISCARD_DRAFT_SUCCESS = 'app/CreatorListingsPage/DISCARD_DRAFT_SUCCESS';
export const DISCARD_DRAFT_ERROR = 'app/CreatorListingsPage/DISCARD_DRAFT_ERROR';

export const SET_ACTIVE_TAB = 'app/CreatorListingsPage/SET_ACTIVE_TAB';

// ================ Reducer ================ //

const initialState = {
  listingIds: [],
  queryInProgress: false,
  queryLoaded: false,
  queryListingsError: null,
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  discardingDraft: null,
  discardingDraftError: null,
  activeTab: 'published', // 'published' or 'draft'
};

export default function creatorListingsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryInProgress: true,
        queryListingsError: null,
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        listingIds: payload.listingIds,
        queryInProgress: false,
        queryLoaded: true,
      };
    case FETCH_LISTINGS_ERROR:
      return {
        ...state,
        queryInProgress: false,
        queryListingsError: payload,
      };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...state,
        openingListing: null,
      };
    case OPEN_LISTING_ERROR:
      return {
        ...state,
        openingListing: null,
        openingListingError: payload,
      };

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...state,
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR:
      return {
        ...state,
        closingListing: null,
        closingListingError: payload,
      };

    case DISCARD_DRAFT_REQUEST:
      return {
        ...state,
        discardingDraft: payload.listingId,
        discardingDraftError: null,
      };
    case DISCARD_DRAFT_SUCCESS:
      return {
        ...state,
        discardingDraft: null,
        // Remove the discarded listing from the list
        listingIds: state.listingIds.filter(id => id.uuid !== payload.listingId),
      };
    case DISCARD_DRAFT_ERROR:
      return {
        ...state,
        discardingDraft: null,
        discardingDraftError: payload,
      };

    case SET_ACTIVE_TAB:
      return {
        ...state,
        activeTab: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const queryListingsRequest = () => ({
  type: FETCH_LISTINGS_REQUEST,
});

export const queryListingsSuccess = listingIds => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { listingIds },
});

export const queryListingsError = error => ({
  type: FETCH_LISTINGS_ERROR,
  payload: error,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = listingId => ({
  type: OPEN_LISTING_SUCCESS,
  payload: { listingId },
});

export const openListingError = error => ({
  type: OPEN_LISTING_ERROR,
  payload: error,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = listingId => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: { listingId },
});

export const closeListingError = error => ({
  type: CLOSE_LISTING_ERROR,
  payload: error,
});

export const discardDraftRequest = listingId => ({
  type: DISCARD_DRAFT_REQUEST,
  payload: { listingId },
});

export const discardDraftSuccess = listingId => ({
  type: DISCARD_DRAFT_SUCCESS,
  payload: { listingId },
});

export const discardDraftError = error => ({
  type: DISCARD_DRAFT_ERROR,
  payload: error,
});

export const setActiveTab = tab => ({
  type: SET_ACTIVE_TAB,
  payload: tab,
});

// ================ Thunks ================ //

export const queryListings = () => async (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest());

  try {
    // Query user's own live-room listings
    const response = await sdk.ownListings.query({
      pub_type: config.listingTypes['live-room'],
      states: ['draft', 'pendingApproval', 'published', 'closed'],
      include: ['images', 'author'],
      'fields.image': [
        'variants.square-small',
        'variants.square-small2x',
        'variants.landscape-crop',
        'variants.landscape-crop2x',
      ],
      'limit.images': 1,
    });

    const listings = response.data.data || [];
    const listingIds = listings.map(l => l.id);
    dispatch(addMarketplaceEntities(response));
    dispatch(queryListingsSuccess(listingIds));

    return listings;
  } catch (e) {
    dispatch(queryListingsError(storableError(e)));
    throw e;
  }
};

export const openListing = listingId => async (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  try {
    const response = await listingsAPI.open({ id: listingId }, { expand: true });
    dispatch(addMarketplaceEntities(response));
    dispatch(openListingSuccess(listingId));
    return response;
  } catch (e) {
    dispatch(openListingError(storableError(e)));
    throw e;
  }
};

export const closeListing = listingId => async (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  try {
    const response = await listingsAPI.close({ id: listingId }, { expand: true });
    dispatch(addMarketplaceEntities(response));
    dispatch(closeListingSuccess(listingId));
    return response;
  } catch (e) {
    dispatch(closeListingError(storableError(e)));
    throw e;
  }
};

export const discardDraft = listingId => async (dispatch, getState, sdk) => {
  dispatch(discardDraftRequest(listingId));

  try {
    const response = await sdk.ownListings.discardDraft({ id: listingId });
    dispatch(discardDraftSuccess(listingId));
    return response;
  } catch (e) {
    dispatch(discardDraftError(storableError(e)));
    throw e;
  }
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  // Ensure user is logged in
  await dispatch(fetchCurrentUser());

  // Fetch listings
  return dispatch(queryListings());
};
